import React from "react";
import { Reveal, Overline } from "./Reveal";
import { ShieldCheck, Globe2, Workflow, Sparkles } from "lucide-react";

const STATS = [
  { k: "09", v: "Specialised verticals under one roof" },
  { k: "120+", v: "Vetted vendor relationships globally" },
  { k: "98%", v: "On-time fulfilment across active orders" },
  { k: "24/7", v: "Direct procurement support desk" },
];

const PILLARS = [
  {
    icon: ShieldCheck,
    t: "Single-source accountability",
    d: "One point of contact replaces fragmented vendor management — across every category we supply.",
  },
  {
    icon: Globe2,
    t: "Global reach, local discipline",
    d: "International sourcing pipelines paired with on-the-ground logistics and compliance know-how.",
  },
  {
    icon: Workflow,
    t: "Procurement-grade processes",
    d: "Itemised quotations, audited shipments, full traceability — built for tenders and enterprise procurement.",
  },
  {
    icon: Sparkles,
    t: "Curation over catalogue",
    d: "We don't sell everything. We supply what passes our standards — and we stand behind it.",
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why" data-testid="why-section" className="relative section-y bg-primary text-primary-foreground overflow-hidden">
      <div className="absolute inset-0 -z-10 opacity-[0.06]">
        <div className="absolute -top-32 -right-32 w-[60vw] h-[60vw] rounded-full border border-white/30" />
        <div className="absolute -bottom-32 -left-32 w-[50vw] h-[50vw] rounded-full border border-white/20" />
      </div>

      <div className="max-page container-px">
        <Reveal>
          <Overline className="text-primary-foreground/80">Why One Sphere</Overline>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-primary-foreground">
            Consolidation, without compromise.
          </h2>
          <p className="mt-6 max-w-2xl text-primary-foreground/70 leading-relaxed">
            Why does a developer trust One Sphere with both their cement order
            and their ERP rollout? Because we treat every category with the
            same operating discipline.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 border-y border-white/10 py-14">
          {STATS.map((s, i) => (
            <Reveal key={s.v} delay={i * 0.08}>
              <div data-testid={`stat-${i}`} className="text-center sm:text-left">
                <div className="font-display text-5xl sm:text-6xl lg:text-7xl text-secondary leading-none">
                  {s.k}
                </div>
                <div className="mt-3 text-xs sm:text-sm uppercase tracking-[0.2em] text-primary-foreground/60 max-w-[180px]">
                  {s.v}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.t} delay={i * 0.06}>
                <div
                  data-testid={`pillar-${i}`}
                  className="group p-8 lg:p-10 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm transition-all duration-500"
                >
                  <Icon className="w-7 h-7 text-secondary" strokeWidth={1.4} />
                  <h3 className="mt-6 font-display text-2xl sm:text-3xl text-primary-foreground">
                    {p.t}
                  </h3>
                  <p className="mt-4 text-primary-foreground/65 leading-relaxed text-sm sm:text-base">
                    {p.d}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
